import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import styled from "styled-components";

import { gql } from "@examspark/api";
import { assertNativeError } from "@examspark/common";
import { FlexBox, VSpace } from "@examspark/common-ui";
import { AppLicense } from "@examspark/types";

import {
  GoogleConnectResponse,
  useGoogleSignIn,
} from "../../../hooks/useGoogleSignIn";
import { logger } from "../../../util";

import GoogleButton from "./GoogleButton";

const Connect = styled(FlexBox)`
  background-color: #f5f5e2;
  padding: 1rem;
  border-radius: 1rem;
  flex-direction: column;
  gap: 1rem;

  img {
    width: 100%;
    border: solid 1px #ddd;
    border-radius: 1rem;
  }
  p {
    line-height: 1.8rem;
  }
  *:first-child {
    margin-top: 0;
  }
`;

interface Props {
  onConnect?: (rsp: GoogleConnectResponse) => void;
  appKey?: AppLicense["appKey"];
  userId?: string;
}

const GoogleConnect: React.FC<
  React.PropsWithChildren<PropsWithChildren<Props>>
> = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const gsi = useGoogleSignIn({
    tokenServerUrl: process.env.REACT_APP_REST_API_URL || "",
  });

  const init = useCallback(async () => {
    try {
      if (!props.appKey || !props.userId) return;
      setIsLoading(true);

      const appUserConfig = await gql.operations.app.getUserConfig(
        props.appKey,
        props.userId,
      );

      const refreshToken =
        appUserConfig?.config?.googleClassroom?.auth?.refresh_token;
      if (!refreshToken) throw new Error("No refresh token found");

      const tokens = await gsi.refreshAccessToken(refreshToken);
      gsi.verifyScopes(gsi.scopes.classroom, tokens.scope.split(" "));
      props.onConnect?.(tokens);
    } catch (error) {
      assertNativeError(error);
      console.error(error.message);
    } finally {
      setIsLoading(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.appKey, props.userId]);

  useEffect(() => {
    init();
  }, [props.appKey, props.userId, init]);

  /**
   * connect(), then save user config, then hit the `token` API * route using
   * `code` from result to generate an `access_token` * and `refresh_token`.
   * Save the `refresh_token` to the server!
   */
  const connect = () => {
    gsi.connect(
      {
        scopes: gsi.scopes.classroom,
      },
      async (rsp) => {
        if (!props.appKey) {
          console.error("App key is required to connect to Google Classroom");
          alert("App key is required to connect to Google Classroom");
          return;
        }
        if (!props.userId) {
          console.error("User ID is required to connect to Google Classroom");
          alert("User ID is required to connect to Google Classroom");
          return;
        }
        // confirm we were granted all the scopes
        const grantedScopes = rsp.scope.split(" ");
        const grantedAllScopes = gsi.verifyScopes(
          gsi.scopes.classroom,
          grantedScopes,
        );

        if (!grantedAllScopes) {
          alert(
            "Authentication was not successful. Try re-connecting to your Google Classroom account and allow access to all permissions requested, to continue.",
          );
          logger.error(
            "Authentication to Google Classroom failed due to improper permission scope.",
            {},
          );
          return;
        }

        gapi.client.setToken({ access_token: rsp.access_token });
        await gql.operations.app.setUserConfig(props.appKey, props.userId, {
          googleClassroom: {
            enabled: true,
            auth: {
              refresh_token: rsp.refresh_token,
              scope: rsp.scope,
            },
          },
        });

        logger.info("Setting app-user-config with Google Classroom token", {});
        props.onConnect?.(rsp);
      },
    );
  };

  return (
    <Connect>
      <VSpace>
        <div>
          {props.children ?? (
            <>
              <p>
                In order to publish your activities to classes in Google
                Classroom, you must first allow ExamSpark to access your
                Classroom account. Click Sign In to Google, then when prompted
                to select what ExamSpark can access, select all 6 permissions.
              </p>
            </>
          )}
        </div>
        {isLoading ? (
          <div>Connecting to Google Classroom...</div>
        ) : (
          <div>
            <GoogleButton title="Login to Google" onClick={() => connect()} />
          </div>
        )}
      </VSpace>
    </Connect>
  );
};

export default GoogleConnect;
